import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ModuleGuide } from "./ModuleGuide";
import { StepBar, Primary, Footer, Num, PropertySelect, Stat, eur } from "./ui";

const LABELS = ["Bien", "Charges récurrentes", "Revenus"];
const FREQS = ["Mensuelle", "Trimestrielle", "Annuelle"];

const ChargesStep = ({ catalog, sel, setSel }) => (
  <div className="space-y-2" data-testid="fin-charges">
    {catalog.map((c) => {
      const v = sel[c.key];
      return (
        <div key={c.key} className={`rounded-2xl border px-4 py-3 ${v ? "border-brand-limeDark bg-brand-lime/10" : "border-brand-soft"}`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!v} onChange={(e) => setSel({ ...sel, [c.key]: e.target.checked ? { amount: "", frequency: c.frequency } : undefined })} data-testid={`fin-check-${c.key}`} />
            <span className="flex-1 text-sm font-medium text-brand-greenDeep">{c.label}</span>
          </label>
          {v && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Num label="Montant" suffix="€" optional value={v.amount} onChange={(a) => setSel({ ...sel, [c.key]: { ...v, amount: a } })} testid={`fin-amount-${c.key}`} />
              <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Fréquence</span>
                <select className="input" value={v.frequency} onChange={(e) => setSel({ ...sel, [c.key]: { ...v, frequency: e.target.value } })} data-testid={`fin-freq-${c.key}`}>{FREQS.map((f) => <option key={f}>{f}</option>)}</select>
              </label>
            </div>
          )}
        </div>
      );
    })}
  </div>
);

const RentsStep = ({ pid }) => {
  const [d, setD] = useState(null);
  const load = () => api.get("/guides/finances/rents", { params: { property_id: pid } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const pay = async (ids) => { for (const id of ids) await api.put(`/receipts/${id}/pay`).catch(() => {}); toast.success("Loyers marqués encaissés"); load(); };
  if (!d) return null;
  return (
    <div data-testid="fin-rents">
      <p className="text-sm text-brand-green/70 mb-3">{d.paid_count} loyer(s) déjà encaissé(s). Marquez en un clic ceux que vous avez déjà reçus :</p>
      {d.pending.length === 0 ? <p className="text-sm text-brand-green/50" data-testid="fin-rents-empty">Aucun loyer en attente.</p> : (
        <>
          <ul className="divide-y divide-brand-soft border border-brand-soft rounded-2xl">
            {d.pending.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                <span className="flex-1 text-brand-greenDeep">{r.month} · {r.tenant}</span><b>{eur(r.amount)}</b>
                <button onClick={() => pay([r.id])} className="px-3 py-1.5 rounded-full bg-brand-softer hover:bg-brand-soft text-xs font-semibold" data-testid={`fin-pay-${r.id}`}>Encaissé</button>
              </li>
            ))}
          </ul>
          <button onClick={() => pay(d.pending.map((r) => r.id))} className="mt-3 text-sm font-semibold text-brand-greenDeep underline underline-offset-4" data-testid="fin-pay-all">Tout marquer encaissé</button>
        </>
      )}
    </div>
  );
};

const OneOff = ({ p }) => {
  const [f, setF] = useState(null);
  if (!f) return <button onClick={() => setF({ label: "", amount: "", date: new Date().toISOString().slice(0, 10) })} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-greenDeep" data-testid="fin-oneoff-open"><Plus className="w-4 h-4" /> Ajouter une dépense ponctuelle (travaux, frais…)</button>;
  const save = async () => {
    await api.post("/transactions", { type: "Dépense", category: "Charge déductible : Travaux d'entretien et de réparation", property: p.name, owner: p.owner || "", amount: Number(f.amount) || 0, date: f.date, label: f.label || "Dépense ponctuelle", status: "Payé" });
    toast.success("Dépense ajoutée"); setF(null);
  };
  return (
    <div className="grid sm:grid-cols-3 gap-3 items-end" data-testid="fin-oneoff">
      <label className="block"><span className="block text-xs font-medium text-brand-green/70 mb-1">Libellé</span><input className="input" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} data-testid="fin-oneoff-label" /></label>
      <Num label="Montant" suffix="€" value={f.amount} onChange={(a) => setF({ ...f, amount: a })} testid="fin-oneoff-amount" />
      <button onClick={save} className="px-4 py-3 rounded-full bg-brand-greenDeep text-white text-sm font-semibold" data-testid="fin-oneoff-save">Ajouter</button>
    </div>
  );
};

const Steps = ({ properties, onDone }) => {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [pid, setPid] = useState(properties[0]?.id);
  const [catalog, setCatalog] = useState([]);
  const [sel, setSel] = useState({});
  const [cash, setCash] = useState(null);
  const [busy, setBusy] = useState(false);
  const p = properties.find((x) => x.id === pid) || {};
  useEffect(() => { api.get("/guides/finances/catalog").then((r) => setCatalog(r.data.filter((c) => c.key !== "cfe" || /meubl/i.test(p.rental_type || "")))); }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveCharges = async () => {
    setBusy(true);
    const items = Object.entries(sel).filter(([, v]) => v).map(([key, v]) => ({ key, amount: Number(v.amount) || 0, frequency: v.frequency }));
    try { if (items.length) await api.post("/guides/finances/expenses", { property_id: pid, items }); setStep(3); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const finish = async () => { const r = await api.get("/guides/finances/cashflow", { params: { property_id: pid } }); setCash(r.data); setStep(4); };
  if (step === 4 && cash) return (
    <div className="pb-6" data-testid="fin-success">
      <CheckCircle2 className="w-10 h-10 text-brand-limeDark" />
      <h3 className="font-display text-2xl text-brand-greenDeep mt-3">Ce que « {cash.property} » vous rapporte vraiment</h3>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <Stat label="Cash-flow mensuel" value={eur(cash.monthly)} tone={cash.monthly < 0 ? "text-red-600" : ""} testid="fin-cash-monthly" />
        <Stat label="Cash-flow annuel" value={eur(cash.annual)} tone={cash.annual < 0 ? "text-red-600" : ""} testid="fin-cash-annual" />
      </div>
      <p className="text-xs text-brand-green/60 mt-2">Loyers {eur(cash.rent)} − charges {eur(cash.expenses)}{cash.has_loan ? ` − prêt ${eur(cash.loan)}` : ""} par mois.</p>
      <div className="mt-5 space-y-3"><OneOff p={p} /></div>
      <Footer>
        {!cash.has_loan && <button onClick={() => { onDone(); nav("/app/finances/prets?guide=loans"); }} className="px-4 py-3 rounded-full border border-brand-soft text-sm font-semibold" data-testid="fin-add-loan">Ajoutez votre prêt pour un cash-flow complet</button>}
        <Primary onClick={onDone} data-testid="fin-done">Terminer</Primary>
      </Footer>
    </div>
  );
  return (
    <div data-testid="fin-steps">
      <StepBar step={step} total={3} labels={LABELS} />
      {step === 1 && <PropertySelect properties={properties} value={pid} onChange={setPid} />}
      {step === 2 && <><p className="text-sm text-brand-green/70 mb-3">Cochez vos charges habituelles. Le montant est facultatif : vous pourrez l'ajouter plus tard. Les échéances futures sont créées automatiquement.</p><ChargesStep catalog={catalog} sel={sel} setSel={setSel} /></>}
      {step === 3 && <RentsStep pid={pid} />}
      <Footer onBack={step > 1 ? () => setStep(step - 1) : null}>
        {step === 1 && <Primary onClick={() => setStep(2)} data-testid="fin-next-1">Continuer</Primary>}
        {step === 2 && <Primary busy={busy} onClick={saveCharges} data-testid="fin-next-2">Continuer</Primary>}
        {step === 3 && <Primary onClick={finish} data-testid="fin-next-3">Voir mon cash-flow</Primary>}
      </Footer>
    </div>
  );
};

const Preview = () => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
    <Stat label="Loyers" value="780 €" /><Stat label="Charges" value="−145 €" /><Stat label="Cash-flow / mois" value="635 €" />
  </div>
);

export const FinancesGuide = () => (
  <ModuleGuide module="finances" label="Guide Finances" intro="Suivez ce que chaque bien vous rapporte vraiment."
    benefit="En 3 minutes : vos charges récurrentes, vos loyers encaissés et votre cash-flow réel." preview={<Preview />} Steps={Steps} />
);
